import Box from "@mui/material/Box";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef } from "react";

const VIRTUAL_SCROLLER_CLASS = ".MuiDataGrid-virtualScroller";
const VIRTUAL_SCROLLER_CONTENT_CLASS = ".MuiDataGrid-virtualScrollerContent";
const SCROLL_THRESHOLD = 1;

interface DataGridScrollShadowProps {
  children: ReactNode;
}

// A wrapper component that adds Stripe-style horizontal scrolling shadows to a DataGrid.
// The shadows only appear on the table body (rows), not on the header or footer/pagination.
// This works by setting CSS custom properties on the .MuiDataGrid-virtualScrollerContent element,
// which are used by the theme's pseudo-elements for the shadow effect.
export function DataGridScrollShadow({ children }: DataGridScrollShadowProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const virtualScrollerRef = useRef<Element | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const updateShadows = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const virtualScroller = container.querySelector(VIRTUAL_SCROLLER_CLASS);
    if (!virtualScroller) {
      return;
    }

    const virtualScrollerContent = container.querySelector<HTMLElement>(
      VIRTUAL_SCROLLER_CONTENT_CLASS,
    );

    // The scroller's own `scrollWidth` overshoots the rows by a few pixels,
    // because the grid's overlay scrollbars are children of it too. The rows can
    // only be scrolled as far as their content is wider than the viewport, so
    // that width is what the shadows follow.
    const { scrollLeft, clientWidth } = virtualScroller;
    const contentWidth =
      virtualScrollerContent?.getBoundingClientRect().width ??
      virtualScroller.scrollWidth;
    const scrollableWidth = contentWidth - clientWidth;

    const shouldShowLeftShadow = scrollableWidth > 0 && scrollLeft > 0;
    const shouldShowRightShadow =
      scrollableWidth > 0 && scrollLeft < scrollableWidth - SCROLL_THRESHOLD;

    if (virtualScrollerContent) {
      virtualScrollerContent.style.setProperty(
        "--datagrid-left-shadow-opacity",
        shouldShowLeftShadow ? "1" : "0",
      );
      virtualScrollerContent.style.setProperty(
        "--datagrid-right-shadow-opacity",
        shouldShowRightShadow ? "1" : "0",
      );
    }
  }, []);

  const setupVirtualScroller = useCallback(() => {
    const container = containerRef.current;
    if (!container) {
      return false;
    }

    const virtualScroller = container.querySelector(VIRTUAL_SCROLLER_CLASS);
    if (!virtualScroller) {
      return false;
    }

    if (virtualScrollerRef.current === virtualScroller) {
      return true;
    }

    if (virtualScrollerRef.current) {
      virtualScrollerRef.current.removeEventListener("scroll", updateShadows);
      resizeObserverRef.current?.unobserve(virtualScrollerRef.current);
    }

    virtualScrollerRef.current = virtualScroller;
    virtualScroller.addEventListener("scroll", updateShadows);
    resizeObserverRef.current?.observe(virtualScroller);
    updateShadows();

    return true;
  }, [updateShadows]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      setupVirtualScroller();
      updateShadows();
    });
    resizeObserverRef.current = resizeObserver;
    resizeObserver.observe(container);

    if (!setupVirtualScroller()) {
      let attempts = 0;
      const maxAttempts = 10;
      const intervalId = setInterval(() => {
        attempts++;
        if (setupVirtualScroller() || attempts >= maxAttempts) {
          clearInterval(intervalId);
        }
      }, 100);
    }

    return () => {
      if (virtualScrollerRef.current) {
        virtualScrollerRef.current.removeEventListener("scroll", updateShadows);
      }
      resizeObserver.disconnect();
      virtualScrollerRef.current = null;
      resizeObserverRef.current = null;
    };
  }, [setupVirtualScroller, updateShadows]);

  return (
    <Box ref={containerRef} sx={{ height: "100%" }}>
      {children}
    </Box>
  );
}
